import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";

import { Picture } from "@/components/wpl/image/src/index";
import { Link } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";

const eligibleContainerCss = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    textAlign: "center"
});

const headerCss = css({
    fontSize: "32px",
    lineHeight: "38px",
    fontWeight: 800,
    margin: "0 0 14px 0",
    [media.above.sm]: {
        fontSize: "48px",
        lineHeight: "48px",
        margin: "0 0 16px 0"
    }
});

const subtitleCss = css({
    fontSize: "16px",
    lineHeight: "24px",
    fontWeight: 400,
    margin: "0 auto 10px",
    [media.above.sm]: {
        fontSize: "20px",
        lineHeight: "28px",
        maxWidth: "768px",
        margin: "0 auto 20px"
    },
    [media.above.md]: {
        fontSize: "28px",
        lineHeight: "40px",
        maxWidth: "100%"
    }
});

const rowWrapperCss = css({
    "display": "flex",
    "flexWrap": "wrap",
    "justifyContent": "space-between",
    "listStyleType": "none",
    "padding": "0",
    "margin": 0,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& > li": {
        width: "50%",
        boxSizing: "border-box",
        padding: "16px",
        [media.above.sm]: {
            width: "25%"
        }
    }
});

const cardTitleCss = css({
    fontSize: "20px",
    lineHeight: "28px",
    margin: "0 0 12px",
    [media.above.sm]: {
        fontSize: "24px",
        lineHeight: "30px"
    }
});

const cardContentCss = css({
    margin: "0",
    maxWidth: "266px"
});

const cardContentLinkCss = css({
    color: wacToken("color.primary.blue"),
    textDecoration: "underline"
});

const ctaBlockCss = css({
    display: "flex",
    justifyContent: "center",
    width: "100%",
    margin: "50px 0 0",
    backgroundSize: "140% auto",
    backgroundRepeat: "no-repeat, no-repeat",
    padding: "55px 16px",
    position: "relative",
    backgroundImage:
        'url("https://dam-cdn.atl.orangelogic.com/AssetLink/xo68oe60v01q41q2ac4j00105878i48l.webp")',
    [media.only.sm]: {
        backgroundPosition: "left top , right -75px bottom",
        backgroundSize: "699px auto",
        padding: "96px 16px",
        height: "218px"
    },
    [media.above.sm]: {
        backgroundImage:
            'url("https://dam-cdn.atl.orangelogic.com/AssetLink/04451t30ps6lpg487gb3ml40mcmb3rj6.webp")',
        backgroundPosition: "center",
        backgroundSize: "729px auto"
    }
});

const ctaBlockContentCss = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    [media.above.sm]: {
        padding: "16px 16px 32px"
    }
});

const ctaBlockTitleCss = css({
    fontSize: "24px",
    lineHeight: "30px",
    margin: "0"
});

const ctaBlockDetailCss = css({
    fontSize: "16px",
    lineHeight: "24px"
});

const imageCss = css({
    maxWidth: "112px",
    maxHeight: "116px",
    position: "absolute",
    insetInlineEnd: "-5%",
    insetBlockEnd: "-10%",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["img"]: {
        width: "75%",
        height: "75%"
    },
    [media.only.sm]: {
        insetInlineEnd: "15%",
        insetBlockEnd: "10%",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ["img"]: {
            width: "100%",
            height: "100%"
        }
    },
    [media.above.md]: {
        insetInlineEnd: "20%",
        insetBlockEnd: "15%"
    }
});
export const EligibleSection = () => {
    const intl = useIntl();

    return (
        <div css={eligibleContainerCss}>
            <h2 css={headerCss}>
                <FormattedMessage
                    id="NonprofitPage.eligible.header"
                    defaultMessage="Who’s eligible"
                />
            </h2>
            <h3 css={subtitleCss}>
                <FormattedMessage
                    id="NonprofitPage.eligible.subtitle"
                    defaultMessage="If you’re ready to move beyond the free license, 75% discounts on Atlassian Cloud apps are available to nonprofit organizations that are:"
                />
            </h3>
            <ul css={rowWrapperCss}>
                <li>
                    <Picture
                        image={{
                            src: "https://dam-cdn.atl.orangelogic.com/AssetLink/4vunf5vd4cboe8glor3vep3mam3a3d0o.svg",
                            alt: ""
                        }}
                    />
                    <h4 css={cardTitleCss}>
                        <FormattedMessage
                            id="NonprofitPage.card.title1"
                            defaultMessage="Non-governmental"
                        />
                    </h4>
                    <p css={cardContentCss}>
                        <FormattedMessage
                            id="NonprofitPage.card.content1"
                            defaultMessage="independent from government control"
                        />
                    </p>
                </li>
                <li>
                    <Picture
                        image={{
                            src: "https://dam-cdn.atl.orangelogic.com/AssetLink/1665m6o0xxrphegnh7lnwf17585pj211.svg",
                            alt: ""
                        }}
                    />
                    <h4 css={cardTitleCss}>
                        <FormattedMessage
                            id="NonprofitPage.card.title2"
                            defaultMessage="Non-commercial"
                        />
                    </h4>
                    <p css={cardContentCss}>
                        <FormattedMessage
                            id="NonprofitPage.card.content2"
                            defaultMessage="operating on a not-for-profit basis and for the public benefit"
                        />
                    </p>
                </li>
                <li>
                    <Picture
                        image={{
                            src: "https://dam-cdn.atl.orangelogic.com/AssetLink/k1y270i4t5e33yp8hw62tn3l37850050.svg",
                            alt: ""
                        }}
                    />
                    <h4 css={cardTitleCss}>
                        <FormattedMessage
                            id="NonprofitPage.card.title3"
                            defaultMessage="Non-political"
                        />
                    </h4>
                    <p css={cardContentCss}>
                        <FormattedMessage
                            id="NonprofitPage.card.content3"
                            defaultMessage="or against any political party, candidate, or campaign"
                        />
                    </p>
                </li>
                <li>
                    <Picture
                        image={{
                            src: "https://dam-cdn.atl.orangelogic.com/AssetLink/8q882ia8oh77fl481x8f01up4y2a2q18.svg",
                            alt: ""
                        }}
                    />
                    <h4 css={cardTitleCss}>
                        <FormattedMessage
                            id="NonprofitPage.card.title4"
                            defaultMessage="Recognized as a nonprofit"
                        />
                    </h4>
                    <p css={cardContentCss}>
                        <FormattedMessage
                            id="NonprofitPage.card.content4"
                            defaultMessage="in the country in which you are registered, in accordance with the "
                        />
                        <a
                            css={cardContentLinkCss}
                            target="_blank"
                            href="https://goodstack.org/nonprofit-definitions"
                            rel="noreferrer"
                        >
                            <FormattedMessage
                                id="NonprofitPage.card.content4.link"
                                defaultMessage="eligibility criteria for that country"
                            />
                        </a>
                    </p>
                </li>
            </ul>
            <div css={ctaBlockCss}>
                <div css={ctaBlockContentCss}>
                    <h3 css={ctaBlockTitleCss}>
                        <FormattedMessage
                            id="NonprofitPage.eligible.cta.title"
                            defaultMessage="Need more info?"
                        />
                    </h3>
                    <p css={ctaBlockDetailCss}>
                        <FormattedMessage
                            id="NonprofitPage.eligible.cta.content"
                            defaultMessage="Get the answers for your most frequently asked eligibility questions."
                        />
                    </p>
                    <Link
                        text={intl.formatMessage({
                            id: "NonprofitPage.eligible.cta.content.link",
                            defaultMessage: "Learn more"
                        })}
                        href="#faq"
                        type="primary"
                        dataEventProps={{
                            "data-event-component": "linkButton",
                            "data-event-container": "Eligibility section",
                            "data-event": "clicked",
                            "data-product-key": "nonProfitCalculator"
                        }}
                    />
                    <div css={imageCss}>
                        <Picture
                            image={{
                                src: "https://dam-cdn.atl.orangelogic.com/AssetLink/8fhb444317uecxj0n4dy144u8atr503c.webp",
                                alt: ""
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
EligibleSection.displayName = "EligibleSection";
